<template>
  <layout v-if="profileLoaded">
    <template v-slot:title
      >{{ title }}<help-link page="content-blocks"
    /></template>
    <template v-slot:header-buttons>
      <router-link
        v-if="canCreateContentBlocks"
        :to="{ name: 'CreateContentBlock' }"
        class="btn btn-primary"
        >Create</router-link
      >
    </template>
    <div class="mb-1">
      <switch-field
        id="updated-only"
        v-model="showOnlyUpdated"
        inline
        label="Show updated blocks only"
      />
    </div>
    <filter-control label="Filter content blocks" v-model="filter" />
    <div
      v-if="allTags.length > 0"
      class="d-flex flex-justify-center flex-align-center flex-wrap"
    >
      <tag
        v-for="t in allTags"
        class="cursor-pointer"
        :key="t.tag"
        :tag="t.tag"
        :count="t.count"
        :selected="t.selected"
        @click="tagClicked(t)"
      />
    </div>
    <refresh-all
      v-if="showOnlyUpdated && contentBlocks.length > 0"
      @refresh-all="displayRefreshAllModal"
    />
    <table class="table cells-top">
      <thead>
        <table-header
          @sort-click="sortClicked"
          :current-sort-field="sortField"
        ></table-header>
      </thead>
      <tbody>
        <table-row
          v-for="(contentBlock, index) in contentBlocks"
          :index="index"
          :key="contentBlock.systemName"
          :content-block="contentBlock"
          @block-select="
            (systemName) => blockSelected(systemName, 'showContentBlockModal')
          "
          @block-refresh="
            (systemName) => blockSelected(systemName, 'showRefreshModal')
          "
          @update-content="
            (systemName) => blockSelected(systemName, 'showUpdateContentModal')
          "
        />
      </tbody>
    </table>
    <pager
      :count="pages"
      :current-page="currentPage"
      @page-click="pageClicked"
    ></pager>
    <template v-slot:modal>
      <content-block-modal
        v-if="showContentBlockModal"
        :content-block="currentBlock"
        @insert="insertContentBlock"
        @add-tag="insertContentBlockAsTag"
        @close="modalClosed"
      />
      <business-rule-modal
        v-if="showRuleModal"
        title="Insert Content Block"
        @continue="ruleSelected"
        @close="ruleModalClosed"
      />
      <refresh-modal
        v-if="showRefreshModal"
        :content-block="currentBlock"
        @update-block="displayRefreshConfirmModal"
        @close="cancelRefreshContentBlocks"
      />
      <update-content-modal
        v-if="showUpdateContentModal"
        :content-block="currentBlock"
        @close="cancelUpdateContent"
        @update-content="displayUpdateContentConfirmModel"
      />
      <confirm-modal
        v-if="showRefreshConfirmModal"
        message="Do you want to update your document's content?"
        @confirm="refreshContentBlocks"
        @cancel="cancelRefreshContentBlocks"
      />
      <confirm-modal
        v-if="showRefreshAllModal"
        message="Do you want to update all of your document's content?"
        @confirm="refreshContentBlocks(true)"
        @cancel="cancelRefreshContentBlocks"
      />
      <confirm-modal
        v-if="showUpdateContentConfirmModal"
        message="Do you want to update update sharedo with this content?"
        :params="updateContentParams"
        @confirm="updateContent"
        @cancel="cancelUpdateContent"
      />
    </template>
  </layout>
</template>
<script>
import documentModel from "@/mixins/documentModel";
import contentBlocksMixin from "@/mixins/contentBlocks";
import refreshContentMixin from "@/mixins/refreshContent";
import profile from "@/mixins/profile";
import contentBlocks from "@/services/sharedo/contentBlocksService";
import contentControls from "@/services/word/contentControlsService";
import customProperties from "@/services/word/customPropertiesService";
import docGenTemplates from "@/services/sharedo/docGenTemplatesService";
import { direction, ascending, descending } from "@/util/sort";
import FilterControl from "@/components/common/filtering/FilterControl.vue";
import Pager from "@/components/common/pagination/Pager.vue";
import ContentBlockModal from "@/components/content-blocks/ContentBlockModal.vue";
import TableHeader from "@/components/content-blocks/TableHeader.vue";
import TableRow from "@/components/content-blocks/TableRow.vue";
import { PAGE_SIZE } from "@/constants/pagination";
import { union } from "@/util/arrayUtil";
import Tag from "@/components/common/tags/Tag.vue";
import BusinessRuleModal from "@/components/business-rules/BusinessRuleModal.vue";
import RefreshModal from "@/components/content-blocks/RefreshModal.vue";
import UpdateContentModal from "@/components/content-blocks/UpdateContentModal.vue";
import ConfirmModal from "@/components/common/confirmations/ConfirmModal.vue";
import RefreshAll from "@/components/content-blocks/RefreshAll.vue";
import HelpLink from "@/components/common/help/HelpLink.vue";
import {
  CONTENT_BLOCKS_CREATE,
  ADMIN_DOCUMENT_ASSEMBLY,
} from "@/constants/permissions";
import {
  CONTENT_BLOCK_DOCUMENT,
  CONTENT_BLOCK_HTML,
} from "@/constants/templateTypes";
import { withErrorHandling } from "@/util/sharedoUtil";
import { toSaveRequest } from "@/mappers/sharedo/content-blocks/contentBlockMapper";
import {
  FOR_TEMPLATE_AUTHORS,
  FOR_DOCUMENT_AUTHORS,
} from "@/constants/contentBlockIncludes";

const reload = async function () {
  await this.loadTags();
  await this.loadContentBlocks();
};

const loadContentBlocks = async function (goToStart = false) {
  this.logDebug("Loading content blocks.");

  const sharedoId = await customProperties.get("SharedoActivityId");

  const opts = {
    filter: this.filter,
    sort: {
      by: this.sortField,
      direction: this.sortDirection,
    },
    paging: {
      page: goToStart ? 1 : this.currentPage,
      pageSize: PAGE_SIZE,
    },
    tags: this.selectedTags.map((t) => t.tag),
    includeOpenXmlTemplates: !sharedoId,
  };

  if (this.showOnlyUpdated) {
    opts.paging = {};
    opts.systemNames = this.currentBlocks.map((block) => block.systemName);
  }

  if (this.capabilities.separateContentBlocks && this.include) {
    opts.includeTemplateAuthorContentBlocks =
      (this.include & FOR_TEMPLATE_AUTHORS) === FOR_TEMPLATE_AUTHORS;
    opts.includeDocumentAuthorContentBlocks =
      (this.include & FOR_DOCUMENT_AUTHORS) === FOR_DOCUMENT_AUTHORS;
  }

  try {
    const result = await contentBlocks.get(opts);
    this.logInfo("Loaded content blocks.", result);

    if (this.showOnlyUpdated) {
      this.pages = 1;

      if (this.currentBlocks.length > 0) {
        this.contentBlocks = result.results.filter((serverBlock) =>
          this.currentBlocks.some(
            (localBlock) =>
              localBlock.systemName === serverBlock.systemName &&
              localBlock.version.number < serverBlock.versionNumber
          )
        );
      } else {
        this.contentBlocks = [];
      }
    } else {
      this.pages = Math.ceil(result.totalCount / PAGE_SIZE);
      this.contentBlocks = result.results;
    }

    if (goToStart) {
      this.currentPage = 1;
    }
  } catch (error) {
    this.logError("Unable to load content blocks.", { error });
    this.$store.commit("error", { message: "Unable to load content blocks." });
  }
};

const loadTags = async function () {
  this.matchedTags = await contentBlocks.getTags(this.filter);
};

const data = function () {
  return {
    filter: "",
    sortField: "title",
    sortDirection: direction.ASCENDING,
    showOnlyUpdated: false,
    pages: 0,
    currentPage: 1,
    contentBlocks: [],
    currentBlock: null,
    matchedTags: [],
    selectedTags: [],
    showRuleModal: false,
    showContentBlockModal: false,
    showUpdateContentModal: false,
    showUpdateContentConfirmModal: false,
    updateContentParams: {},
  };
};

const mounted = async function () {
  await this.loadTags();
  await this.loadContentBlocks();
};

const tagClicked = async function (t) {
  if (t.selected) {
    const tag = this.selectedTags.find((st) => st.tag === t.tag);
    this.selectedTags.splice(this.selectedTags.indexOf(tag), 1);
  } else {
    this.selectedTags.push({ tag: t.tag, count: t.count });
  }

  await this.loadTags();
  await this.loadContentBlocks();
};

const pageClicked = async function (pageNo) {
  this.currentPage = pageNo;
  await this.loadContentBlocks();
};

const sortClicked = async function (args) {
  this.sortField = args.field;
  this.sortDirection = args.direction;
  await this.loadContentBlocks();
};

const blockSelected = async function (systemName, modalProp) {
  try {
    const selectedBlock = this.contentBlocks.find(
      (cb) => cb.systemName === systemName
    );

    if (selectedBlock.templateType === CONTENT_BLOCK_HTML) {
      const sharedoId = await customProperties.get("SharedoActivityId");
      const result = await contentBlocks.loadOne(systemName, sharedoId);
      this.currentBlock = {
        ...result,
        title: selectedBlock.title,
        templateType: selectedBlock.templateType,
        versionNumber: selectedBlock.versionNumber,
        versionDate: selectedBlock.versionDate,
        availableForDocumentAuthors: selectedBlock.availableForDocumentAuthors,
        availableForTemplateAuthors: selectedBlock.availableForTemplateAuthors,
      };
    } else {
      this.currentBlock = { ...selectedBlock };
    }

    this[modalProp] = true;
    this.logDebug("Content block selected.", { systemName });
  } catch (error) {
    this.logError("Error selecting content block.", { systemName, error });
    this.$store.commit("error", { message: "Unable to load content block." });
  }
};

const displayUpdateContentConfirmModel = async function (
  contentBlock,
  saveStatus = "published"
) {
  this.updateContentParams = { saveStatus };

  if (saveStatus === "published") {
    this.showUpdateContentModal = false;
    this.showUpdateContentConfirmModal = true;
  } else {
    await this.updateContent(this.updateContentParams);
  }
};

const cancelUpdateContent = function () {
  this.showUpdateContentModal = false;
  this.showUpdateContentConfirmModal = false;
};

const updateContent = async function ({ saveStatus }) {
  this.logDebug("Updating sharedo with content from block.", {
    systemName: this.currentBlock.systemName,
  });
  try {
    const blockToUpdate = await docGenTemplates.getOne(
      this.currentBlock.systemName
    );

    this.logInfo("Retrieved block to update from sharedo.", {
      contentBlock: blockToUpdate,
    });

    const contentControlItems =
      this.documentModel.findContentControlBySystemName(
        blockToUpdate.systemName
      );
    if (contentControlItems.length > 0) {
      const html = await contentControls.getHtmlContent(
        contentControlItems[0].id
      );

      this.logDebug("Extracted HTML from the content block.", { html });

      blockToUpdate.documentPackContents[0].sources[0].htmlContent = html;

      await docGenTemplates.save(
        blockToUpdate.systemName,
        toSaveRequest(blockToUpdate, saveStatus)
      );

      this.logInfo("Updated sharedo with the new content.", {
        contentBlock: blockToUpdate,
        html,
      });

      const versions = await docGenTemplates.getSourceVersions(
        blockToUpdate.documentPackContents[0].sources[0].id
      );
      versions.sort(descending((v) => v.versionNumber));

      this.contentBlocks.find(
        (cb) => cb.systemName === this.currentBlock.systemName
      ).versionNumber = versions[0].versionNumber;

      contentControlItems[0].version = {
        number: versions[0].versionNumber,
        date: versions[0].createdDate,
      };

      this.logInfo(
        "Updated content block with new version number from sharedo.",
        { version: versions[0] }
      );

      this.saveDocumentModel(this.documentModel);
      this.logDebug("Document model saved.");

      this.showUpdateContentModal = false;
      this.showUpdateContentConfirmModal = false;
    }
  } catch (error) {
    console.log(error);
    this.$store.commit("error", { message: "Unable to update content block." });
  }
};

export default {
  props: {
    include: {
      type: [Number, String],  // Route params are strings
      required: false,
    },
  },
  components: {
    TableHeader,
    TableRow,
    FilterControl,
    Pager,
    ContentBlockModal,
    Tag,
    BusinessRuleModal,
    RefreshAll,
    RefreshModal,
    UpdateContentModal,
    ConfirmModal,
    HelpLink,
  },
  mixins: [profile, documentModel, contentBlocksMixin, refreshContentMixin],
  data,
  computed: {
    title: function () {
      // Use == for loose comparison (route param is string, constant is number)
      if (this.include == FOR_DOCUMENT_AUTHORS) {
        return "Clauses";
      }

      return "Content Blocks";
    },
    allTags: function () {
      const matched = this.matchedTags.map((t) => ({
        tag: t.tag,
        count: t.count,
        selected: false,
      }));
      const selected = this.selectedTags.map((t) => ({
        tag: t.tag,
        count: t.count,
        selected: true,
      }));
      const all = union(selected, matched, (t1, t2) => t1.tag === t2.tag);
      all.sort(ascending((t) => t.tag));

      return all;
    },
    canCreateContentBlocks: function () {
      return this.hasPermission(CONTENT_BLOCKS_CREATE);
    },
    isTemplateAuthor: function () {
      return this.hasPermission(ADMIN_DOCUMENT_ASSEMBLY);
    },
  },
  watch: {
    showOnlyUpdated: async function () {
      await this.loadContentBlocks(true);
    },
    filter: async function () {
      await this.loadTags();
      await this.loadContentBlocks();
    },
    include: async function () {
      await this.loadTags();
      await this.loadContentBlocks();
    },
  },
  methods: {
    reload,
    pageClicked,
    sortClicked,
    blockSelected,
    loadContentBlocks: withErrorHandling(loadContentBlocks),
    loadTags: withErrorHandling(loadTags),
    tagClicked,
    cancelUpdateContent,
    displayUpdateContentConfirmModel,
    updateContent,
  },
  mounted,
};
</script>
